import type { SceneId } from "./types";
import type { Tick } from "./clock";
import type { EventBus, GameEvents } from "./event-bus";

/** Интерфейс сцены — lifecycle hooks + update. */
export interface Scene {
  readonly id: SceneId;
  onEnter(): Promise<void> | void;
  onExit(): Promise<void> | void;
  onUpdate?(ctx: Tick): void;
}

/**
 * Минимальный интерфейс для анимированного перехода между сценами.
 * Определён в core/, чтобы SceneManager не зависел от rendering/.
 * Полный AnimationController в rendering/ расширяет этот интерфейс.
 */
export interface SceneTransitionProvider {
  sceneTransition(from: HTMLElement | null, to: HTMLElement): Promise<void>;
}

/** Опции SceneManager. */
export interface SceneManagerOptions {
  /** Провайдер анимированных переходов. Если не задан — переходы мгновенные. */
  animation?: SceneTransitionProvider;
  /** Функция получения DOM-элемента сцены по id. */
  getElement?: (id: SceneId) => HTMLElement | null;
}

/** Конечный автомат сцен. Эмитит scene:change через EventBus. */
export class SceneManager {
  private readonly scenes = new Map<SceneId, Scene>();
  private currentScene: Scene | null = null;

  constructor(
    private readonly bus: EventBus<GameEvents>,
    private readonly options: SceneManagerOptions = {},
  ) {}

  /** Регистрирует сцену по её id. */
  register(scene: Scene): void {
    this.scenes.set(scene.id, scene);
  }

  /** Выполняет переход: onExit текущей, анимированный переход (если задан), onEnter новой, эмитит scene:change. */
  async transitionTo(id: SceneId): Promise<void> {
    const next = this.scenes.get(id);
    if (!next) throw new Error(`Scene not registered: ${id}`);
    const from = this.currentScene?.id ?? id;

    const fromEl = from !== id ? (this.options.getElement?.(from) ?? null) : null;
    const toEl = this.options.getElement?.(id) ?? null;

    if (this.currentScene) await this.currentScene.onExit();

    if (this.options.animation && toEl) {
      await this.options.animation.sceneTransition(fromEl, toEl);
    }

    this.currentScene = next;
    await next.onEnter();
    if (from !== id) this.bus.emit("scene:change", { from, to: id });
  }

  /** Возвращает текущую сцену или null. */
  current(): Scene | null {
    return this.currentScene;
  }

  /** Возвращает id текущей сцены или null. */
  currentId(): SceneId | null {
    return this.currentScene?.id ?? null;
  }

  /** Прокидывается из GameLoop степа каждый тик. */
  update(ctx: Tick): void {
    this.currentScene?.onUpdate?.(ctx);
  }
}
